import { useEffect, useState } from 'react';
import { Typography, Button, InputNumber, Spin, Alert, message } from 'antd';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';

const { Text } = Typography;

// Margenes: el margen por defecto de la tienda para cada lista de venta. Lo
// usan los productos en automático que no tienen uno propio. Guardar recalcula
// todos esos productos, que es para lo que se cambia un margen.
export const Margenes = () => {
  const { theme } = useClient();
  const primary = theme?.primaryColor || '#0f172a';
  const primaryText = theme?.primaryTextColor || '#fff';

  const [listas, setListas] = useState(null);
  const [autos, setAutos] = useState(0);
  const [valores, setValores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    const res = await api.admin.precios.getMargenes();
    if (res.status === 'success') {
      setListas(res.data.listas || []);
      setAutos(res.data.productos_automaticos || 0);
      setValores(Object.fromEntries((res.data.listas || []).map((l) => [l.id_lista, l.margen])));
    } else {
      setError(res.message || 'No se pudieron cargar los márgenes');
    }
  };

  useEffect(() => { cargar().catch(() => setError('Error de red')); }, []);

  const cambios = (listas || []).some((l) => (valores[l.id_lista] ?? null) !== (l.margen ?? null));

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await api.admin.precios.setMargenes(
        Object.fromEntries(listas.map((l) => [l.id_lista, valores[l.id_lista] ?? null])),
      );
      if (res.status === 'success') {
        const n = res.data?.precios_actualizados || 0;
        message.success(n ? `Guardado. Se actualizaron ${n} precio${n === 1 ? '' : 's'}.` : 'Guardado. Ningún precio cambió.');
        await cargar();
      } else {
        message.error(res.message || 'No se pudo guardar');
      }
    } catch {
      message.error('Error de red');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <Text strong style={{ fontSize: 28, lineHeight: 1.2, display: 'block', marginBottom: 8 }}>Márgenes</Text>
      <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 20 }}>
        El margen por defecto de cada lista. Un producto en precio automático vende a costo más este margen,
        salvo que tenga uno propio. Hoy hay {autos} producto{autos === 1 ? '' : 's'} en automático.
      </Text>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      {!listas && !error && <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>}

      {listas && (
        <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '4px 16px' }}>
          {listas.map((l, i) => (
            <div key={l.id_lista} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '12px 0', borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
            }}>
              <Text style={{ fontSize: 14 }}>{l.nombre}</Text>
              <InputNumber
                value={valores[l.id_lista]} onChange={(v) => setValores((x) => ({ ...x, [l.id_lista]: v }))}
                min={-99.99} step={1} suffix="%" placeholder="Sin margen" style={{ width: 140 }}
                aria-label={`Margen ${l.nombre}`}
              />
            </div>
          ))}
        </div>
      )}

      {listas && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sin margen, los productos en automático no generan precio para esa lista.
          </Text>
          <Button type="primary" disabled={!cambios} loading={guardando} onClick={guardar}
            style={cambios ? { background: primary, borderColor: primary, color: primaryText } : undefined}>
            Guardar y recalcular
          </Button>
        </div>
      )}
    </div>
  );
};
